import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog } from "./Dialog";
import { View } from ".";

type FormModalProps<T> = {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal should close (Cancel or X button) */
  onClose: () => void;
  /** Called with the updated config when Save is clicked */
  onSave: (config: T) => void;
  /** The initial config values */
  config: T;
  /** Modal title */
  title: string;
  /** Modal size */
  size?: "sm" | "md" | "lg" | "flex";
  /** Additional className for the modal */
  className?: string;
  /** Label for the save button. Defaults to "Save" */
  saveLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel" */
  cancelLabel?: string;
  /** Loading state for the save button */
  isSaving?: boolean;
  /** Render prop that receives localConfig and onChange handler */
  children: (props: { localConfig: T; onChange: (key: string) => (value: unknown) => void }) => React.ReactNode;
};

/**
 * FormModal - A standardized modal component for form-based dialogs.
 *
 * Handles local state management automatically:
 * - Creates a local copy of config when modal opens
 * - Resets to original config on Cancel
 * - Commits changes on Save
 *
 * @example
 * ```tsx
 * <FormModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSave={(config) => dispatch(updateConfig(config))}
 *   config={currentConfig}
 *   title="Settings"
 * >
 *   {({ localConfig, onChange }) => (
 *     <MyForm config={localConfig} onChange={onChange} />
 *   )}
 * </FormModal>
 * ```
 */
export function FormModal<T>({
  isOpen,
  onClose,
  onSave,
  config,
  title,
  size = "flex",
  className = "w-full max-w-[800px] min-w-0 max-h-[90vh] mx-4",
  saveLabel = "Save",
  cancelLabel = "Cancel",
  isSaving = false,
  children,
}: FormModalProps<T>) {
  // Local copy of config - only committed on Save
  const [localConfig, setLocalConfig] = useState<T>(config);
  const wasOpen = useRef(false);

  // Reset local config only when modal opens (transitions from closed to open)
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setLocalConfig(config);
    }
    wasOpen.current = isOpen;
  }, [isOpen, config]);

  // Create onChange handler for form fields
  const onChange = useCallback(
    (key: string) => (value: unknown) => {
      setLocalConfig(
        (prev) =>
          ({
            ...prev,
            [key]: value,
          }) as T,
      );
    },
    [],
  );

  const handleSave = () => {
    onSave(localConfig);
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      title={title}
      size={size}
      className={className}
      saveLabel={saveLabel}
      cancelLabel={cancelLabel}
      isSaving={isSaving}
    >
      <View className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">{children({ localConfig, onChange })}</View>
    </Dialog>
  );
}
